import { Controller } from "@hotwired/stimulus"

// Generation Poll Controller - Polls for background generation progress
//
// Usage:
//   <div data-controller="generation-poll"
//        data-generation-poll-url-value="/panel/draft_articles/123/generation_status"
//        data-generation-poll-interval-value="2000"
//        data-generation-poll-reload-on-complete-value="true">
//     <div data-generation-poll-target="progressBar" style="width: 0%"></div>
//     <span data-generation-poll-target="percent">0%</span>
//     <span data-generation-poll-target="detail">Starting...</span>
//   </div>
//
export default class extends Controller {
  static targets = ["progressBar", "percent", "detail", "status"]
  static values = {
    url: String,
    interval: { type: Number, default: 3000 },
    reloadOnComplete: { type: Boolean, default: true }
  }

  connect() {
    this.poll()
    this.startPolling()
  }

  disconnect() {
    this.stopPolling()
  }

  startPolling() {
    this.pollTimer = setInterval(() => this.poll(), this.intervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) return

      const data = await response.json()
      this.updateUI(data)

      // Check if generation is complete
      if (data.completed) {
        this.stopPolling()

        if (data.status === 'completed' && this.reloadOnCompleteValue) {
          // Add a small delay to let the user see the 100% completion
          setTimeout(() => {
            window.location.reload()
          }, 1000)
        } else if (data.status === 'failed') {
          // For failures, reload to show the error state
          window.location.reload()
        }
      }
    } catch (error) {
      console.error("Generation polling error:", error)
    }
  }

  updateUI(data) {
    // Update progress bar width
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${data.percent}%`
    }

    // Update percent text
    if (this.hasPercentTarget) {
      this.percentTarget.textContent = `${data.percent}%`
    }

    // Update detail text
    if (this.hasDetailTarget && data.detail) {
      this.detailTarget.textContent = data.detail
    }

    // Update status text if target exists
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.humanizeStatus(data.status)
    }
  }

  humanizeStatus(status) {
    const statusMap = {
      'idle': 'Idle',
      'queued': 'Queued',
      'initializing': 'Initializing',
      'generating_outline': 'Creating Outline',
      'generating_sections': 'Writing Content',
      'generating_images': 'Generating Images',
      'reviewing': 'Reviewing',
      'completed': 'Completed',
      'failed': 'Failed',
      'cancelled': 'Cancelled'
    }
    return statusMap[status] || status
  }
}
